// The numeric and date filter editor: a condition, then one bound (two for Between). Numeric edits commit
// 900ms after the last edit; a complete date commits at once. An invalid draft keeps its inline error and
// never commits, so the last valid committed filter stays in effect.

import React, { useId } from 'react'
import PropTypes from 'prop-types'

import { Button } from '@CommonComponents/buttons'
import { useTable } from '../context'
import { FILTER_ERRORS, changeRangeMode, isFilterActive } from '../query'
import {
  MODE_LABELS,
  NUMERIC_COMMIT_MS,
  checkDraft,
  commitFilter,
  draftFrom,
  invalidBounds,
  useDrafts,
} from './drafts'

const boundLabels = (type, between) => {
  if (type === 'date') return between ? ['From', 'To'] : ['Date', null]
  return between ? ['Minimum', 'Maximum'] : ['Value', null]
}

export const RangeEditor = ({ column, type }) => {
  const { state, dispatch } = useTable()
  const store = useDrafts()
  const id = useId()
  const { key } = column
  const committed = state.query.filters[key]
  const record = store.record(key)
  const draft = record ? record.draft : draftFrom(type, committed)
  const error = record ? record.error : null
  const between = draft.mode === 'between'
  const numeric = type === 'numeric'

  // commits read the committed value at the moment they run, not at render
  const save = (next, nextError, basis) =>
    store.write(key, { draft: next, error: nextError, basis })
  const later = next =>
    store.schedule(
      key,
      () => {
        const checked = checkDraft(type, next)
        if (checked.error) save(next, checked.error)
        else {
          save(next, null, checked.value)
          commitFilter(dispatch, store.committed(key), key, checked.value)
        }
      },
      NUMERIC_COMMIT_MS
    )

  const onMode = mode => {
    const next = changeRangeMode(type, draft, mode)
    next.from = next.from ?? ''
    next.to = next.to ?? ''
    next.bad = { from: Boolean(draft.bad && draft.bad.from) }
    if (next.mode === 'between')
      next.bad.to = Boolean(draft.bad && draft.bad.to)
    const checked = checkDraft(type, next)
    if (checked.error) {
      store.cancel(key)
      save(next, checked.error)
    } else if (numeric) {
      save(next, null)
      later(next)
    } else {
      save(next, null, checked.value)
      commitFilter(dispatch, store.committed(key), key, checked.value)
    }
  }

  const onBound = (which, event) => {
    const { value, validity } = event.target
    const next = {
      ...draft,
      [which]: value,
      bad: {
        ...(draft.bad || {}),
        [which]: Boolean(validity && validity.badInput),
      },
    }
    const checked = checkDraft(type, next)
    if (numeric) {
      // a correction clears the error now; a new error waits for the commit
      save(next, checked.error ? error : null)
      later(next)
      return
    }
    if (!checked.error) {
      save(next, null, checked.value)
      commitFilter(dispatch, store.committed(key), key, checked.value)
      return
    }
    // a reversed range shows at once; a date still being typed shows when focus leaves it
    save(next, checked.error === FILTER_ERRORS.range ? checked.error : error)
  }

  // a partial date reports no change, so its validity is read when focus leaves the input
  const onBlur = (which, event) => {
    if (numeric) return
    const { validity } = event.target
    const unreadable = Boolean(validity && validity.badInput)
    const next =
      unreadable === Boolean(draft.bad && draft.bad[which])
        ? draft
        : { ...draft, bad: { ...(draft.bad || {}), [which]: unreadable } }
    const checked = checkDraft(type, next)
    if (checked.error && (next !== draft || checked.error !== error))
      save(next, checked.error)
    else if (next !== draft) save(next, error)
  }

  const clear = () => {
    store.drop(key)
    commitFilter(dispatch, committed, key, null)
  }

  const invalid = invalidBounds(type, draft, error)
  const errorId = `${id}-error`
  const [firstLabel, secondLabel] = boundLabels(type, between)
  const input = (which, label) => (
    <label
      className={`me-table-filter-field${invalid[which] ? ' has-error' : ''}`}
    >
      <span className="me-table-filter-field-label">{label}</span>
      <input
        className="form-control"
        type={numeric ? 'text' : 'date'}
        inputMode={numeric ? 'decimal' : undefined}
        autoComplete="off"
        value={draft[which]}
        aria-invalid={invalid[which] || undefined}
        aria-describedby={invalid[which] ? errorId : undefined}
        onChange={event => onBound(which, event)}
        onBlur={event => onBlur(which, event)}
      />
    </label>
  )

  return (
    <>
      <fieldset className="me-table-filter-modes">
        <legend className="me-table-filter-legend">Condition</legend>
        {MODE_LABELS[type].map(([mode, label]) => (
          <label key={mode} className="me-panel-row">
            <input
              type="radio"
              name={`${id}-mode`}
              value={mode}
              checked={draft.mode === mode}
              onChange={() => onMode(mode)}
            />
            {label}
          </label>
        ))}
      </fieldset>
      <div className="me-table-filter-bounds">
        {input('from', firstLabel)}
        {between && input('to', secondLabel)}
      </div>
      {error && (
        <div className="has-error">
          <p className="help-block" id={errorId} role="alert">
            {error}
          </p>
        </div>
      )}
      <div className="me-panel-foot">
        <Button
          className="me-btn me-btn-ghost me-btn-xs"
          aria-disabled={(!isFilterActive(committed) && !record) || undefined}
          onClick={clear}
        >
          Clear filter
        </Button>
      </div>
    </>
  )
}

RangeEditor.propTypes = {
  column: PropTypes.object.isRequired,
  type: PropTypes.oneOf(['numeric', 'date']).isRequired,
}
